import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Col, Row, theme, Typography } from 'antd';
import dayJs from 'dayjs';
import SchedulerContainer from 'pos-client/components/customScheduler/components/SchedulerContainer.tsx';
import type {
  AppointmentClickEvent,
  AppointmentContextMenuEvent,
  AppointmentDblClickEvent,
  AppointmentTooltipShowingEvent,
  AppointmentUpdatedEvent,
  AppointmentUpdatingEvent,
  CellContextMenuEvent,
  CellData,
  SchedulerRef,
  TimeCellData,
} from 'pos-client/components/customScheduler/types/schedulerTypes';
import {
  cellDuration,
  editing,
  groups,
  height,
} from 'pos-client/components/scheduleBoard/constants/boardConfig.ts';
import { useScheduleBoardContext } from 'pos-client/components/scheduleBoard/context/ScheduleBoardContext.tsx';
import { useJobStatusColourMapper } from 'pos-client/components/scheduleBoard/hooks/useJobStatusColourMapper.ts';
import { useSelectedLocationCurrentDate } from 'pos-client/components/scheduleBoard/hooks/useSelectedLocationCurrentDate.ts';
import {
  type AppointmentAddingEvent,
  type AppointmentDraggingStartEvent,
} from 'pos-client/components/scheduleBoard/types/boardTypes.ts';
import {
  adaptAddEvent,
  adaptDragStartEvent,
  type ScheduleBoardAppointment,
} from 'pos-client/components/scheduleBoard/types/schedulerTypeAdapters.ts';
import {
  cellTimeIsTradingTime,
  navigateTo,
  tradingTimeStringToMinutes,
} from 'pos-client/components/scheduleBoard/utils/ScheduleBoardUtils.ts';
import { AssignTechnicianModal } from 'pos-client/components/technicians/AssignTechnicianModal.tsx';
import { usePermissions } from 'pos-client/hooks/usePermissions.tsx';
import { useSettingsByPath } from 'pos-client/hooks/useSettings.tsx';
import {
  dayJsWeekdayAsString,
  timeFormatter,
} from 'pos-client/utils/dateHelpers.ts';
import {
  type PagedDataResourceWithAssetModel,
  type ScheduleBoardJobModel,
  ServiceTypesRequestSchema,
} from 'pos-shared/models/schedulingSchema.ts';

import AddNewJobModal, {
  type AddNewJobModalRef,
} from './modals/AddNewJobModal.tsx';
import BlockedSlotModal, {
  type BlockedSlotModalRef,
} from './modals/BlockedSlotModal.tsx';
import {
  type CustomModalPropsData,
  ManageScheduleSettingsModal,
} from './modals/ManageScheduleSettingsModal.tsx';
import {
  type ResourceModel,
  ScheduleBoardResourceCell,
} from './ScheduleBoardResourceCell.tsx';
import { Slot } from './Slot.tsx';

import 'pos-client/components/scheduleBoard/ScheduleBoard.css';
import 'pos-client/components/customScheduler/components/scheduler.css';

type SchedulerProps = {
  handleJobUpdating: (e: AppointmentUpdatingEvent) => void;
  handleJobUpdated: (e: AppointmentUpdatedEvent) => void;
  handleJobDragStart: (e: AppointmentDraggingStartEvent) => void;
  handleJobAdd: (e: AppointmentAddingEvent) => void;
  handleJobDoubleClick: (e: AppointmentDblClickEvent) => void;
  handleJobClick: (e: AppointmentClickEvent) => void;
  hideJobTooltip: (e: AppointmentTooltipShowingEvent) => void;
  resourceData: PagedDataResourceWithAssetModel | undefined;
  resourceDataIsLoading: boolean;
  userIsDragging: boolean;
};

const ScheduleBoard = memo(
  ({
    handleJobUpdating,
    handleJobUpdated,
    handleJobDragStart,
    handleJobAdd,
    handleJobClick,
    handleJobDoubleClick,
    hideJobTooltip,
    resourceData,
    resourceDataIsLoading,
    userIsDragging,
  }: SchedulerProps) => {
    const { t } = useTranslation();
    const { token } = theme.useToken();
    const navigate = useNavigate();
    const { jobStatuses: _jobStatuses } = useJobStatusColourMapper();

    const {
      selectedLocation,
      selectedDate,
      serviceTypeFilter,
      boardData,
      startDayHour,
      endDayHour,
      tradingTimes,
      isNonTradingDate,
      laneConflicts,
      compactView,
      setSelectedTimeSlot,
      setSelectedLaneId,
      setShowScheduleModal,
    } = useScheduleBoardContext();
    const now = useSelectedLocationCurrentDate();

    const [isSettingsModalVisible, setIsSettingsModalVisible] =
      useState<boolean>(false);
    const [isTechnicianModalVisible, setIsTechnicianModalVisible] =
      useState<boolean>(false);
    const [laneIdsWithConflicts, setLaneIdsWithConflicts] = useState<string[]>(
      []
    );

    const { first: defaultSetting } = useSettingsByPath({
      path: 'Features>EnableDefaultLanes',
    });

    const defaultEnabled = defaultSetting?.value === 'true';

    const locationDate = dayJs(selectedDate);
    const locationStartTime = locationDate
      .hour(startDayHour)
      .minute(0)
      .second(0);
    const locationEndTime = locationDate.hour(endDayHour).minute(0).second(0);

    const [modalData, setModalData] = useState<CustomModalPropsData>({
      serviceLocationId: selectedLocation?.id || '',
      resource: {},
      startTime: locationStartTime,
      endTime: locationEndTime,
    });

    const scheduler = useRef<SchedulerRef>(null);
    const blockedSlotModalRef = useRef<BlockedSlotModalRef | null>(null);
    const addNewJobModalRef = useRef<AddNewJobModalRef | null>(null);
    const { hasPermissions } = usePermissions();

    const canAddBlockedSlot = hasPermissions('add.blocked.slot');
    const canEditUnauthorizedBlockedSlot = hasPermissions(
      'edit.unauthorized.blocked.slot'
    );
    const canEditAuthorizedBlockedSlot = hasPermissions(
      'edit.authorized.blocked.slot'
    );

    const canRescheduleJob = hasPermissions('reschedule.job');

    const createOrder = (e: CellContextMenuEvent) => {
      setSelectedTimeSlot(e.cellData.startDate);
      setSelectedLaneId(e.cellData.groups.laneId);
      setShowScheduleModal(false);
      navigate('/orders/create');
    };

    const editLane = (model: ResourceModel) => {
      setModalData({
        resource: model.data,
        startTime: locationStartTime,
        endTime: locationEndTime,
        serviceLocationId: selectedLocation?.id || '',
      });

      if (serviceTypeFilter === ServiceTypesRequestSchema.enum.Static) {
        setIsTechnicianModalVisible(true);
      } else {
        setIsSettingsModalVisible(true);
      }
    };

    const renderResourceCell = (model: ResourceModel) => {
      return (
        <ScheduleBoardResourceCell
          model={model}
          editLane={() => editLane(model)}
          serviceLocationType={serviceTypeFilter}
        />
      );
    };

    const openEditBlockedSlotModal = (
      slotData: AppointmentContextMenuEvent['appointmentData']
    ) => {
      const slotDataTyped = slotData as ScheduleBoardJobModel;
      blockedSlotModalRef.current?.openEditModal({
        startDateTime: String(slotDataTyped.startDate),
        finishDateTime: String(slotDataTyped.endDate),
        blockedSlotId: String(slotDataTyped.id),
        assetId: String(slotDataTyped.laneId),
        description: String(slotDataTyped.description ?? ''),
      });
    };

    const handleJobAndBlockedSlotContextMenu = (
      e: AppointmentContextMenuEvent
    ): void => {
      const { appointmentData } = e;
      const jobData = appointmentData as ScheduleBoardAppointment;
      const completedOrCancelled =
        jobData?.state === 'complete' || jobData?.state === 'cancelled';

      const isBlockedSlot = jobData?.text?.toLowerCase().includes('blocked');
      const isAuthorizedBlockedSlot =
        jobData.state?.toLowerCase() === 'authorized';

      // Job Slot
      if (!isBlockedSlot) {
        if (canRescheduleJob && !completedOrCancelled) {
          // Type guard: only jobs have these properties
          const job = jobData as ScheduleBoardJobModel;
          navigateTo(
            `/orders/manage-orders/${job?.orderId}/reschedule/${job?.bookingReference}/${job?.scheduleId}`
          );
        }
      }

      // Blocked Slot
      if (isBlockedSlot) {
        // Authorized
        if (isAuthorizedBlockedSlot && canEditAuthorizedBlockedSlot) {
          openEditBlockedSlotModal(appointmentData);
        } else if (canEditUnauthorizedBlockedSlot) {
          openEditBlockedSlotModal(appointmentData);
        }
      }
    };

    const handleCellContextMenu = (e: CellContextMenuEvent): void => {
      const action = e.action || 'add-order';

      switch (action) {
        case 'add-order':
          createOrder(e);
          break;

        case 'add-job':
          addNewJobModalRef.current?.openModal(
            e.cellData.groups.laneId,
            e.cellData.startDate
          );
          break;

        case 'add-blocked-slot':
          if (!canAddBlockedSlot) return;
          blockedSlotModalRef.current?.openModal(
            e.cellData.groups.laneId,
            dayJs(e.cellData.startDate)
          );
          break;

        default:
          break;
      }
    };

    const handleOk = async () => {
      setIsSettingsModalVisible(false);
      setIsTechnicianModalVisible(false);
    };

    const handleCancel = () => {
      setModalData({
        serviceLocationId: '',
        resource: {},
        startTime: locationStartTime,
        endTime: locationEndTime,
      });
      setIsSettingsModalVisible(false);
      setIsTechnicianModalVisible(false);
    };

    const selectedDateDayJs = useMemo(
      () => dayJs(selectedDate),
      [selectedDate]
    );

    const selectedDateWeekday = useMemo(
      () => dayJsWeekdayAsString(selectedDateDayJs),
      [selectedDateDayJs]
    );

    const laneData = useMemo(() => {
      return resourceData?.results?.map((lane) => ({
        laneId: lane.id,
        defaultDays: lane.defaultDays,
      }));
    }, [resourceData?.results]);

    const defaultLanes = useMemo(() => {
      if (!defaultEnabled || !laneData) return [];
      return laneData
        .filter((lane) => lane.defaultDays?.includes(selectedDateWeekday))
        .map((lane) => lane.laneId);
    }, [defaultEnabled, laneData, selectedDateWeekday]);

    useEffect(() => {
      const filteredLaneIds =
        laneConflicts
          ?.filter(
            (conflict) =>
              selectedDateDayJs.isSame(dayJs(conflict.date), 'day') &&
              conflict.conflicts.some((item) => !item.acknowledged)
          )
          .map((conflict) => conflict.laneId) ?? [];

      setLaneIdsWithConflicts((prevLaneIds) => {
        const hasChanged =
          filteredLaneIds.length !== prevLaneIds.length ||
          !filteredLaneIds.every((id) => prevLaneIds.includes(id));

        return hasChanged ? filteredLaneIds : prevLaneIds;
      });
    }, [laneConflicts, selectedDateDayJs]);

    const renderCellTemplate = useCallback(
      (data: CellData, _index: number, elm: HTMLElement) => {
        const laneId = data.groups?.laneId;
        const matchedLane = laneData?.find((lane) => lane.laneId === laneId);
        const lanesDefaultDays = defaultEnabled
          ? (matchedLane?.defaultDays ?? [])
          : [];

        const dateStr = data.startDate.toISOString();
        if (dayJs(dateStr).format('mm') === '00') {
          elm.style.borderTop = '1px dashed #c4c4c4';
        }

        if (
          laneId &&
          laneIdsWithConflicts?.includes(laneId) &&
          lanesDefaultDays.includes(selectedDateWeekday)
        ) {
          elm.setAttribute('lane-state', 'conflict-default');
        } else if (laneId && laneIdsWithConflicts?.includes(laneId)) {
          elm.setAttribute('lane-state', 'conflict');
        } else if (lanesDefaultDays.includes(selectedDateWeekday)) {
          elm.setAttribute('lane-state', 'default');
        } else {
          elm.removeAttribute('lane-state');
        }

        elm.style.removeProperty('background');

        return elm;
      },
      [
        laneIdsWithConflicts,
        laneData,
        defaultEnabled,
        selectedDateWeekday,
      ]
    );

    const renderTimeCellTemplate = useCallback(
      (data: TimeCellData) => {
        const mins = dayJs(data.date).minute();

        const time = timeFormatter.format(data.date);

        const dateStr = data.date.toISOString();
        const isOpeningTime = cellTimeIsTradingTime(
          dateStr,
          tradingTimes?.start
        );

        const isClosingTime = cellTimeIsTradingTime(dateStr, tradingTimes?.end);

        if (mins === 0 || mins === 30) {
          return (
            <div
              style={{
                color: isOpeningTime
                  ? token.green6
                  : isClosingTime
                    ? token.red6
                    : undefined,
              }}
            >
              {time}
            </div>
          );
        } else {
          return <div />;
        }
      },
      [tradingTimes, token.green6, token.red6]
    );

    const hasNoLanes = useMemo(() => {
      return (
        !resourceDataIsLoading &&
        (resourceData?.results?.filter((resource) => !!resource?.isActive)
          .length === 0 ||
          resourceData?.results?.length === 0)
      );
    }, [resourceDataIsLoading, resourceData]);

    const jobsToLanes = useMemo(
      () => boardData.map((job) => job.laneId),
      [boardData]
    );

    const lanesToDisplay = useMemo(() => {
      return (
        !resourceDataIsLoading &&
        resourceData?.results?.filter((resource) => {
          if (jobsToLanes.some((laneId) => laneId === resource.id)) {
            return true;
          }

          return !!resource?.isActive;
        })
      );
    }, [resourceDataIsLoading, resourceData, jobsToLanes]);

    const onContentReady = useCallback(() => {
      if (scheduler.current) {
        // Check if now is on the same date as selectedDate
        const isSameDate = now && dayJs(now).isSame(dayJs(selectedDate), 'day');

        if (isSameDate) {
          // Scroll to current time if it's today
          scheduler.current.scrollToDate(now);
        }
      }
    }, [selectedDate, now]);

    useEffect(() => {
      onContentReady();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLocation, selectedDate]);

    return (
      <>
        {hasNoLanes ? (
          <Row
            align="middle"
            justify="center"
            style={{
              borderTop: `1px solid ${token.colorFillSecondary}`,
              height: '70vh',
            }}
            gutter={[token.marginXS, token.marginXS]}
          >
            <Col>
              <FontAwesomeIcon size="2x" icon={faCircleInfo} />
            </Col>
            <Col>
              <Typography.Title level={5} style={{ margin: 0 }}>
                {serviceTypeFilter === 'Static'
                  ? t('scheduling.scheduleBoard.noActiveLanesForLocation')
                  : t(
                      'scheduling.scheduleBoard.noActiveLanesForMobileLocation'
                    )}
              </Typography.Title>
            </Col>
          </Row>
        ) : (
          <SchedulerContainer
            ref={scheduler}
            dataSource={boardData}
            defaultCurrentDate={selectedDate}
            currentDate={selectedDate}
            startDayHour={startDayHour}
            endDayHour={endDayHour}
            groups={groups}
            cellDuration={cellDuration}
            appointmentComponent={Slot}
            height={height}
            editing={editing}
            onAppointmentUpdating={handleJobUpdating}
            onAppointmentUpdated={handleJobUpdated}
            onAppointmentContextMenu={handleJobAndBlockedSlotContextMenu}
            onCellContextMenu={handleCellContextMenu}
            onAppointmentClick={handleJobClick}
            onAppointmentDblClick={handleJobDoubleClick}
            onAppointmentTooltipShowing={hideJobTooltip}
            resourceCellRender={renderResourceCell}
            dataCellTemplate={renderCellTemplate}
            onContentReady={onContentReady}
            disabled={!selectedLocation?.id || isNonTradingDate}
            timeCellTemplate={renderTimeCellTemplate}
            timeZone={selectedLocation?.address?.timeZone ?? ''}
            onAppointmentDragStart={(e) =>
              handleJobDragStart(adaptDragStartEvent(e))
            }
            onAppointmentAdd={(e) => handleJobAdd(adaptAddEvent(e))}
            resources={lanesToDisplay || []}
            tradingTimes={
              tradingTimes
                ? {
                    openingTime: new Date(tradingTimes.start),
                    closingTime: new Date(tradingTimes.end),
                    openingMinutes: tradingTimeStringToMinutes(
                      tradingTimes.start
                    ),
                    closingMinutes: tradingTimeStringToMinutes(
                      tradingTimes.end
                    ),
                  }
                : undefined
            }
            defaultLanes={defaultLanes}
            conflictLanes={laneIdsWithConflicts}
            userIsDragging={userIsDragging}
            compactView={compactView}
          />
        )}
        <AssignTechnicianModal
          data={modalData}
          handleOk={handleOk}
          handleCancel={handleCancel}
          isVisible={isTechnicianModalVisible}
          serviceTypeFilter={serviceTypeFilter}
        />
        <ManageScheduleSettingsModal
          data={modalData}
          handleOk={handleOk}
          handleCancel={handleCancel}
          isVisible={isSettingsModalVisible}
          serviceTypeFilter={serviceTypeFilter}
        />
        <BlockedSlotModal ref={blockedSlotModalRef} />
        <AddNewJobModal ref={addNewJobModalRef} />
      </>
    );
  }
);

export default ScheduleBoard;
